/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { SubmissionResponse } from 'src/openapi';
import {
  evaluateCode,
  fetchCategories,
  fetchCategory,
  fetchProblem,
  fetchProblemResponses,
  fetchProblems,
  pollSubmission,
} from '../actions/codingPractice';
import type { CodingPracticeState } from '../types/codingPractice';

const initialState: CodingPracticeState = {
  problems: [],
  total: 0,
  page: 1,
  isListLoading: false,

  problem: null,
  languages: [],
  isProblemLoading: false,

  responses: [],
  isResponsesLoading: false,

  categories: [],
  overall: null,
  isCategoriesLoading: false,
  category: null,
  isCategoryLoading: false,

  submission: null,
  isSubmitting: false,
  isPolling: false,
};

const codingPracticeSlice = createSlice({
  name: 'codingPractice',
  initialState,
  reducers: {
    setSubmission: (state, action: PayloadAction<SubmissionResponse>) => {
      state.submission = action.payload;
    },
    clearSubmission: (state) => {
      state.submission = null;
    },
    // Optimistically flip a node to solved so the map ticks instantly; the
    // authoritative state is reconciled when the category is refetched.
    markProblemSolved: (state, action: PayloadAction<string>) => {
      const slug = action.payload;
      if (!state.category) return;
      const node = (state.category.problems ?? []).find((p) => p.slug === slug);
      if (node && !node.solved) {
        node.solved = true;
        state.category.solved_problems =
          (state.category.solved_problems ?? 0) + 1;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProblems.pending, (state) => {
        state.isListLoading = true;
      })
      .addCase(fetchProblems.fulfilled, (state, action) => {
        state.isListLoading = false;
        state.problems = action.payload.problems ?? [];
        state.total = action.payload.total ?? 0;
        state.page = action.payload.page ?? 1;
      })
      .addCase(fetchProblems.rejected, (state) => {
        state.isListLoading = false;
      });

    builder
      .addCase(fetchProblem.pending, (state) => {
        state.isProblemLoading = true;
        state.problem = null;
        state.submission = null;
      })
      .addCase(fetchProblem.fulfilled, (state, action) => {
        state.isProblemLoading = false;
        state.problem = action.payload;
        state.languages = action.payload.languages ?? [];
      })
      .addCase(fetchProblem.rejected, (state) => {
        state.isProblemLoading = false;
      });

    builder
      .addCase(fetchCategories.pending, (state) => {
        state.isCategoriesLoading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.isCategoriesLoading = false;
        state.categories = action.payload.categories ?? [];
        state.overall = action.payload.overall ?? null;
      })
      .addCase(fetchCategories.rejected, (state) => {
        state.isCategoriesLoading = false;
      });

    builder
      .addCase(fetchCategory.pending, (state) => {
        state.isCategoryLoading = true;
        state.category = null;
      })
      .addCase(fetchCategory.fulfilled, (state, action) => {
        state.isCategoryLoading = false;
        state.category = action.payload;
      })
      .addCase(fetchCategory.rejected, (state) => {
        state.isCategoryLoading = false;
      });

    builder
      .addCase(fetchProblemResponses.pending, (state) => {
        state.isResponsesLoading = true;
      })
      .addCase(fetchProblemResponses.fulfilled, (state, action) => {
        state.isResponsesLoading = false;
        state.responses = action.payload.responses ?? [];
      })
      .addCase(fetchProblemResponses.rejected, (state) => {
        state.isResponsesLoading = false;
      });

    builder
      .addCase(evaluateCode.pending, (state) => {
        state.isSubmitting = true;
        state.submission = null;
      })
      .addCase(evaluateCode.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submission = action.payload;
      })
      .addCase(evaluateCode.rejected, (state) => {
        state.isSubmitting = false;
      });

    builder
      .addCase(pollSubmission.pending, (state) => {
        state.isPolling = true;
      })
      .addCase(pollSubmission.fulfilled, (state) => {
        state.isPolling = false;
      })
      .addCase(pollSubmission.rejected, (state) => {
        state.isPolling = false;
      });
  },
});

export const { setSubmission, clearSubmission, markProblemSolved } =
  codingPracticeSlice.actions;
export default codingPracticeSlice.reducer;
